import type { CSSProperties, ReactNode } from "react";
import { Icon } from "./Icon";

/* ───────────────────────────────── étiquettes ───────────────────────────── */

export function Tag({
  ton = "", dot = false, children,
}: { ton?: string; dot?: boolean; children: ReactNode }) {
  return (
    <span className={`tag ${ton}`}>
      {dot ? <i className="dot" /> : null}
      {children}
    </span>
  );
}

export function Pill({
  actif, n, onClick, children,
}: { actif?: boolean; n?: number; onClick?: () => void; children: ReactNode }) {
  return (
    <button type="button" className="pill" aria-pressed={actif ? "true" : "false"} onClick={onClick}>
      {children}
      {n != null ? <span className="n">{n}</span> : null}
    </button>
  );
}

/* ─────────────────────────────── choix de page ──────────────────────────────
   Pastilles au bureau, liste déroulante au téléphone : les deux sont rendues,
   la CSS n'en montre qu'une. Rien ne déborde, rien ne défile latéralement. */

export interface OptionSegment<T extends string | null> {
  cle: T;
  nom: string;
  n?: number;
}

export function Segment<T extends string | null>({
  options, valeur, onChange, label,
}: {
  options: OptionSegment<T>[];
  valeur: T;
  onChange: (v: T) => void;
  label?: string;
}) {
  const cle = (o: OptionSegment<T>) => (o.cle == null ? "" : String(o.cle));
  const val = valeur == null ? "" : String(valeur);
  return (
    <div className="segment">
      <div className="seg-pills">
        {options.map((o) => (
          <Pill key={cle(o)} actif={val === cle(o)} n={o.n} onClick={() => onChange(o.cle)}>
            {o.nom}
          </Pill>
        ))}
      </div>
      <select
        className="seg-select"
        aria-label={label ?? "Filtrer"}
        value={val}
        onChange={(e) => {
          const trouve = options.find((o) => cle(o) === e.target.value);
          onChange((trouve ? trouve.cle : null) as T);
        }}
      >
        {options.map((o) => (
          <option key={cle(o)} value={cle(o)}>
            {o.n != null ? `${o.nom} (${o.n})` : o.nom}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ─────────────────────────────── onglets ──────────────────────────────────── */

export function Onglets<T extends string>({
  items, actif, onChange,
}: {
  items: { cle: T; nom: string; n?: number }[];
  actif: T;
  onChange: (cle: T) => void;
}) {
  return (
    <div className="onglets" role="tablist">
      {items.map((o) => (
        <button
          key={o.cle}
          type="button"
          role="tab"
          className="onglet"
          aria-selected={actif === o.cle}
          onClick={() => onChange(o.cle)}
        >
          {o.nom}
          {o.n != null ? <span className="n">{o.n}</span> : null}
        </button>
      ))}
    </div>
  );
}

/* La tuile d'une usine : son logo si elle en a un, ses initiales sinon, à sa
   couleur — la même que celle qui signe ses documents. */

export function Logo({
  src, nom, couleur, taille = 36,
}: { src?: string | null; nom: string; couleur?: string | null; taille?: number }) {
  if (src) {
    return <img className="logo-tuile" src={src} alt="" style={{ width: taille, height: taille }} />;
  }
  const initiales = nom
    .split(/[\s-]+/)
    .map((m) => m[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <span
      className="logo-tuile"
      style={{
        width: taille,
        height: taille,
        background: couleur || "var(--ink)",
        fontSize: Math.round(taille * 0.42),
      }}
    >
      {initiales}
    </span>
  );
}

/* ─────────────────────────────── barres ─────────────────────────────────────
   Une comparaison de grandeurs, pas un graphique : la plus grande donne
   l'échelle, et chaque barre porte sa valeur en clair à droite. */

export interface Barre {
  label: string;
  valeur: number;
  sous?: string;
}

export function Bars({
  data, format, axe,
}: { data: Barre[]; format?: (v: number) => string; axe?: boolean }) {
  const max = Math.max(1, ...data.map((d) => d.valeur));
  const ecrire = (v: number) => (format ? format(v) : String(Math.round(v)));
  if (!data.length) return <Empty titre="Rien à comparer" />;
  return (
    <div>
      <div className="bars">
        {data.map((d) => (
          <div
            className="bar-row"
            key={d.label}
            title={`${d.label} — ${ecrire(d.valeur)}${d.sous ? ` · ${d.sous}` : ""}`}
          >
            <div className="bar-lab">{d.label}</div>
            <div className="bar-track">
              <div
                className="bar-fill"
                style={{ width: `${Math.max(2, (d.valeur / max) * 100)}%` }}
              />
            </div>
            <div>
              <div className="bar-val">{ecrire(d.valeur)}</div>
              {d.sous ? <div className="bar-sub">{d.sous}</div> : null}
            </div>
          </div>
        ))}
      </div>
      {axe ? (
        <div className="axis">
          <span>0</span>
          <span>{ecrire(max)}</span>
        </div>
      ) : null}
    </div>
  );
}

/* ──────────────────────────────── parcours ──────────────────────────────────
   Les étapes d'un document, de la première à la dernière. Au téléphone les
   libellés disparaissent et une légende sous la barre dit où l'on en est. */

/* Chaque étape porte sa teinte : le parcours se colore à mesure qu'il se
   franchit, et l'étape courante se repère sans lire les libellés. */
export function Stepper({
  etapes, courante, tons,
}: { etapes: string[]; courante: string; tons?: string[] }) {
  const i = etapes.indexOf(courante);
  const teinte = (k: number) => {
    const t = tons?.[k];
    if (!t) return undefined;
    return { "--etape": `var(--${t})`, "--etape-ink": `var(--${t})` } as CSSProperties;
  };
  return (
    <div>
      <div className="stepper">
        {etapes.map((e, k) => (
          <div
            key={e}
            className={k < i ? "step done" : k === i ? "step now" : "step"}
            style={k <= i ? teinte(k) : undefined}
          >
            <div className="step-bar" />
            <div className="step-lab">{e}</div>
          </div>
        ))}
      </div>
      <div className="step-cap">
        <b>{courante}</b> · étape {i + 1} sur {etapes.length}
      </div>
    </div>
  );
}

/* ───────────────────────────────── indicateurs ──────────────────────────── */

export function Kpi({ k, v, n }: { k: string; v: ReactNode; n?: ReactNode }) {
  return (
    <div className="kpi">
      <div className="kpi-k">{k}</div>
      <div className="kpi-v">{v}</div>
      {n != null ? <div className="kpi-n">{n}</div> : null}
    </div>
  );
}

export function Kpis({ children }: { children: ReactNode }) {
  return <div className="kpis">{children}</div>;
}

/* ─────────────────────────────── mise en page ───────────────────────────── */

export function Section({
  titre, note, droite, children,
}: { titre: string; note?: ReactNode; droite?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <div className="section-h">
        <h2 className="section-t">{titre}</h2>
        {note != null ? <span className="section-n">{note}</span> : null}
        {droite != null ? <div className="section-d">{droite}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function Banner({ children }: { children: ReactNode }) {
  return (
    <div className="banner">
      <Icon n="info" cls="" />
      <div>{children}</div>
    </div>
  );
}

export function Empty({ titre, detail }: { titre: string; detail?: string }) {
  return (
    <div className="empty">
      <div className="empty-t">{titre}</div>
      {detail ? <div className="empty-d">{detail}</div> : null}
    </div>
  );
}

/* La signature Omnix Trading : le nom dans sa capsule, le suffixe interlettré à
   droite, hors capsule. Les proportions viennent de la charte et tiennent à
   l'em : une seule taille à donner, tout le reste suit. `inverse` pour poser la
   signature sur un fond foncé. */
export function Capsule({
  nom = "OMNIX",
  suffixe = "TRADING",
  taille = 14,
  inverse = false,
}: {
  nom?: string;
  /** `null` pour la capsule seule. */
  suffixe?: string | null;
  taille?: number;
  inverse?: boolean;
}) {
  return (
    <span className={`capsule${inverse ? " inverse" : ""}`} style={{ fontSize: `${taille}px` }}>
      <span className="capsule-pill">{nom}</span>
      {suffixe === null ? null : <span className="capsule-suffixe">{suffixe}</span>}
    </span>
  );
}
